import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { useAuth } from "../Context/AuthProvider";
import { tokens } from "../Common/designTokens";
import CustomizeColumns from "../Component/CustomizeColumns";
import UpdateSecurityPopup from "../Component/SecMasterComponents/UpdateSecurityPopup";
import CreateSecurityPopup from "../Component/SecMasterComponents/CreateSecurityPopup";
import { useSecMasterData } from "../Component/SecMasterComponents/useSecMasterHooks";
import { useSecMasterOptions } from "../Component/SecMasterComponents/useSecMasterOptions";
import { downloadSecMasterCSV, MAX_DOWNLOAD_ROWS } from "../Component/SecMasterComponents/SecMasterUtils";
import SecMasterDownloadConfirmModal from "../Component/SecMasterComponents/SecMasterDownloadConfirmModal";
import SecMasterTable from "../Component/SecMasterComponents/SecMasterTable";
import SecMasterHeader from "../Component/SecMasterComponents/SecMasterHeader";
import { clearConfigIfOutdated } from "../Utils/columnVersionManager";
import { getPreference } from "../Utils/userPreferenceManger";
import useSnackbar from "../Utils/useSnackbar";
import { usePageSubscription } from "../Utils/WebSocketProvider";
import { utcToLocal } from "../Utils/timeConversions";
import { useSecMasterOptionsStore } from "../Store/secMasterOptionsStore";
import { fetchSecMasterItems } from "../http/api";
import { buildSecMasterFilterPayload } from "../Component/SecMasterComponents/useSecMasterSearchHooks";

const allColumns = [
  { key: "identifier", name: "Identifier" },
  { key: "securityDesc", name: "Description" },
  { key: "bondType", name: "Type" },
  { key: "subType", name: "Sub Type" },
  { key: "securityCharge", name: "Factored Securities" },
  { key: "poolFactor", name: "Factor" },
  { key: "sector", name: "Sector" },
  { key: "issuer", name: "Issuer" },
  { key: "currency", name: "Currency" },
  { key: "issueCurrency", name: "Issue Currency" },
  { key: "price", name: "Price" },
  { key: "couponPer", name: "Coupon %" },
  { key: "couponType", name: "Coupon Type" },
  { key: "conventionMethod", name: "Convention Method" },
  { key: "interestPaymentFrequency", name: "Payment Frequency" },
  { key: "issueDate", name: "Issue Date" },
  { key: "maturityDate", name: "Maturity Date" },
  { key: "firstCouponDate", name: "First Coupon Date" },
  { key: "lastCouponDate", name: "Last Coupon Date" },
  { key: "nextCallDate", name: "Next Call Date" },
  { key: "callStartDate", name: "Call Start Date" },
  { key: "isCallable", name: "Callable" },
  { key: "isPerpetual", name: "Perpetual" },
  { key: "isZeroCoupon", name: "Zero Coupon" },
  { key: "isInsured", name: "Insured" },
  { key: "isExemptFromFedTax", name: "Fed Tax Exempt" },
  { key: "govState", name: "State" },
  { key: "incomeSource", name: "Income Source" },
  { key: "spRating", name: "S&P" },
  { key: "moodyRating", name: "Moody's" },
  { key: "underlyingIssuerRating", name: "Underlying Issuer Rating" },
  { key: "modifiedBy", name: "Modified By" },
];

// ---------------------------------------------------------------------------
// Maps a FilterChips deletion key (built from the backend payload) back to the
// raw filter-data field(s) to clear when a chip is removed.
const SEC_MASTER_CHIP_TO_FILTER_FIELDS = {
  searchString: ["identifier"],
  minCoupon: ["couponMin"],
  maxCoupon: ["couponMax"],
  minMaturity: ["maturityMin"],
  maxMaturity: ["maturityMax"],
  minPrice: ["priceMin"],
  maxPrice: ["priceMax"],
  minYtm: ["yieldMin"],
  maxYtm: ["yieldMax"],
  minDuration: ["duration", "durationComparison"],
  maxDuration: ["duration", "durationComparison"],
  minRatingSP: ["minimumRating"],
  maxRatingSP: ["minimumRating"],
  minRatingMoody: ["minimumRatingMoody"],
  maxRatingMoody: ["minimumRatingMoody"],
};

// Extract the raw (watchlist-shaped) filter draft carried in navigation state.
const deriveAppliedFilterData = (navState) => {
  const incoming = navState?.secMasterFilterData;
  if (!incoming || typeof incoming !== "object") return null;
  return { ...incoming, bondType: navState?.bondType || incoming.bondType };
};

const SecMasterScreen = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const customizeButtonRef = useRef(null);
  const { showSuccess, showError, SnackbarComponent } = useSnackbar();
  const { options: fieldOptions, refetch: refetchOptions, error: filterOptionsError } = useSecMasterOptions();

  useEffect(() => {
    if (filterOptionsError) showError(filterOptionsError);
  }, [filterOptionsError, showError]);
  usePageSubscription("secMaster");

  const [wsOverrides, setWsOverrides] = useState({});
  const [wsNewRows, setWsNewRows] = useState([]);
  // Identifiers removed by a WS modification that pushed the security out of the
  // active filter set. Filtered out of the rendered rows + the bracket count.
  const [wsRemovedIds, setWsRemovedIds] = useState([]);
  const [wsLastUpdatedAt, setWsLastUpdatedAt] = useState(null);

  // Refs so the stable WS handler always reads the latest token, filters, and
  // the set of identifiers currently on screen without needing to re-register
  // the event listener on every change.
  const appliedFiltersRef = useRef(null);
  const tokenRef = useRef(token);
  const displayedIdentifiersRef = useRef(new Set());

  useEffect(() => {
    const handler = (event) => {
      // WS contract: { secMaster: { ...row fields }, lastUpdatedAt, isNew }.
      // isNew=1 → brand-new security. isNew=0 (or absent) → modification.
      const update = event.detail;
      const row = update?.secMaster ?? update;
      const identifier = row?.identifier;
      if (!identifier) return;

      if (update?.lastUpdatedAt) setWsLastUpdatedAt(update.lastUpdatedAt);

      const filters = appliedFiltersRef.current;
      const prependNewRow = () =>
        setWsNewRows((prev) =>
          prev.some((r) => r.identifier === identifier) ? prev : [row, ...prev]
        );

      // --- isNew=1: brand-new security ---
      if (update?.isNew === 1) {
        if (!filters) {
          // No filters active — add directly to the top.
          prependNewRow();
          return;
        }
        // Filters active — ask the backend whether this identifier matches them.
        // Capture the filter reference so we can detect if filters changed by
        // the time the async response arrives (stale-result guard).
        const capturedFilters = filters;
        fetchSecMasterItems(tokenRef.current, { ...filters, searchString: identifier, page: 0, size: 1 })
          .then((data) => {
            if (appliedFiltersRef.current !== capturedFilters) return;
            if ((data?.totalElements ?? 0) > 0) prependNewRow();
            // 0 matches → ignore the new-security update.
          })
          .catch(() => {});
        return;
      }

      // --- isNew=0 (or absent): modification of an existing security ---
      // Act only if the security is currently on screen (fetched page rows or
      // WS-added rows). Otherwise skip entirely.
      if (!displayedIdentifiersRef.current.has(identifier)) return;

      if (!filters) {
        // On screen, no filters — merge the update into the existing row.
        setWsOverrides((prev) => ({ ...prev, [identifier]: row }));
        return;
      }

      // On screen + filters active — re-check the identifier against the filters.
      const capturedFilters = filters;
      fetchSecMasterItems(tokenRef.current, { ...filters, searchString: identifier, page: 0, size: 1 })
        .then((data) => {
          if (appliedFiltersRef.current !== capturedFilters) return;
          if ((data?.totalElements ?? 0) > 0) {
            // Still matches the filters — merge the update in place.
            setWsOverrides((prev) => ({ ...prev, [identifier]: row }));
            return;
          }
          // No longer matches the filters — remove it from the screen and drop
          // it from any WS-added rows / overrides so the bracket count reflects
          // the removal.
          setWsRemovedIds((prev) =>
            prev.includes(identifier) ? prev : [...prev, identifier]
          );
          setWsNewRows((prev) => prev.filter((r) => r.identifier !== identifier));
          setWsOverrides((prev) => {
            if (!(identifier in prev)) return prev;
            const next = { ...prev };
            delete next[identifier];
            return next;
          });
        })
        .catch(() => {});
    };
    window.addEventListener("secMasterSecurityUpdate", handler);
    return () => window.removeEventListener("secMasterSecurityUpdate", handler);
  }, []);

  useEffect(() => {
    const handler = () => refetchOptions();
    window.addEventListener("secMasterFilterRefresh", handler);
    return () => window.removeEventListener("secMasterFilterRefresh", handler);
  }, [refetchOptions]);

  // Warm the search screen's filter options + default ranges in the background
  // (after the list fetch) so the search screen opens instantly without refetching.
  const preloadSearchData = useSecMasterOptionsStore((s) => s.preload);
  useEffect(() => {
    preloadSearchData(token);
  }, [token, preloadSearchData]);

  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [ccOpen, setCcOpen] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [downloadConfirmOpen, setDownloadConfirmOpen] = useState(false);
  const [pendingDownloadFormat, setPendingDownloadFormat] = useState("xlsx");
  const [updateOpen, setUpdateOpen] = useState(false);
  const [selectedSecurity, setSelectedSecurity] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  // refreshKey stays constant: create/update never force a list refetch — the
  // WS security-update events keep the grid in sync. Kept only so the
  // useSecMasterData positional signature is unchanged.
  const [refreshKey] = useState(0);

  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [sortConfigForFetch, setSortConfigForFetch] = useState(null);

  // Applied search filters (raw watchlist-shaped draft) arrive from the SecMaster
  // search screen via navigation state. Initialize from nav state so the first
  // fetch is already filtered (no unfiltered flash).
  const [appliedFilterData, setAppliedFilterData] = useState(() =>
    deriveAppliedFilterData(location.state)
  );

  // Skip the mount run (the initializer already consumed nav state); react only
  // to later in-place navigations to /sec-master (e.g. a nav-bar click clearing
  // filters, or a fresh Apply while still mounted).
  const didConsumeInitialNavState = useRef(false);
  useEffect(() => {
    if (!didConsumeInitialNavState.current) {
      didConsumeInitialNavState.current = true;
      return;
    }
    setAppliedFilterData(deriveAppliedFilterData(location.state));
    setPage(0);
  }, [location.state]);

  // Backend filter payload or null when nothing is applied. Uses the SecMaster-
  // specific builder so identifier → searchString is included when set.
  const appliedFilters = useMemo(() => {
    if (!appliedFilterData || !appliedFilterData.bondType) return null;
    return buildSecMasterFilterPayload(appliedFilterData);
  }, [appliedFilterData]);

  // Keep refs current so the WS handler always reads the latest values.
  // Also flush any WS-prepended rows when filters change so stale rows from a
  // previous filter set never bleed into the new view.
  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
    setWsNewRows([]);
    setWsRemovedIds([]);
  }, [appliedFilters]);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const [selectedColumns, setSelectedColumns] = useState(() => {
    clearConfigIfOutdated("sec-master", allColumns);
    const saved = getPreference("selectedColumns_sec-master") || [...allColumns];
    if (!saved.some((col) => col.key === "identifier")) {
      const identifierCol = allColumns.find((col) => col.key === "identifier");
      if (identifierCol) return [identifierCol, ...saved];
    }
    return saved;
  });

  const { secMasterData, totalElements, lastUpdatedAt, loading, error: dataError } = useSecMasterData(
    token,
    page,
    itemsPerPage,
    sortConfigForFetch,
    refreshKey,
    appliedFilters
  );

  // Show whichever timestamp is most recent: the one from the last fetch or the
  // one carried by a live WS security-update.
  const displayLastUpdatedAt = useMemo(() => {
    const fetched = utcToLocal(lastUpdatedAt);
    const live = utcToLocal(wsLastUpdatedAt);
    if (!live) return lastUpdatedAt;
    if (!fetched) return wsLastUpdatedAt;
    return live.valueOf() >= fetched.valueOf() ? wsLastUpdatedAt : lastUpdatedAt;
  }, [lastUpdatedAt, wsLastUpdatedAt]);

  const mergedSecMasterData = useMemo(() => {
    const removedSet = wsRemovedIds.length > 0 ? new Set(wsRemovedIds) : null;
    const hasOverrides = Object.keys(wsOverrides).length > 0;
    const applyOverride = (row) => {
      const override = wsOverrides[row.identifier];
      return override ? { ...row, ...override } : row;
    };

    let baseRows = secMasterData;
    if (removedSet) baseRows = baseRows.filter((row) => !removedSet.has(row.identifier));
    if (hasOverrides) baseRows = baseRows.map(applyOverride);

    if (wsNewRows.length === 0) return baseRows;
    const fetchedIds = new Set(secMasterData.map((r) => r.identifier));
    let freshNewRows = wsNewRows.filter(
      (r) => !fetchedIds.has(r.identifier) && !(removedSet && removedSet.has(r.identifier))
    );
    if (hasOverrides) freshNewRows = freshNewRows.map(applyOverride);
    return freshNewRows.length > 0 ? [...freshNewRows, ...baseRows] : baseRows;
  }, [secMasterData, wsOverrides, wsNewRows, wsRemovedIds]);

  // Keep the WS handler's view of on-screen identifiers in sync with what's
  // actually rendered (fetched rows + WS-added rows, minus removals).
  useEffect(() => {
    displayedIdentifiersRef.current = new Set(
      mergedSecMasterData.map((r) => r.identifier)
    );
  }, [mergedSecMasterData]);

  // WS-added rows not yet reflected in totalElements from the API.
  const wsDisplayedNewCount = useMemo(() => {
    if (wsNewRows.length === 0) return 0;
    const fetchedIds = new Set(secMasterData.map((r) => r.identifier));
    const removedSet = new Set(wsRemovedIds);
    return wsNewRows.filter(
      (r) => !fetchedIds.has(r.identifier) && !removedSet.has(r.identifier)
    ).length;
  }, [wsNewRows, secMasterData, wsRemovedIds]);

  // Fetched rows removed by a WS modification — subtracted from the bracket count.
  const wsRemovedFetchedCount = useMemo(() => {
    if (wsRemovedIds.length === 0) return 0;
    const removedSet = new Set(wsRemovedIds);
    return secMasterData.filter((r) => removedSet.has(r.identifier)).length;
  }, [wsRemovedIds, secMasterData]);

  const displayedTotalElements = Math.max(
    0,
    (totalElements || 0) + wsDisplayedNewCount - wsRemovedFetchedCount
  );

  const handleSort = (columnKey, direction) => {
    setSortConfig({ key: columnKey, direction });
    setSortConfigForFetch({ columnToSort: columnKey, sortingDirection: direction });
  };

  const handlePageChange = (event, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (event) => {
    setItemsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCusipClick = (index, rowData) => {
    // The sec-master row already carries every editable/display field, so we
    // open the Update Security popup directly against the row data.
    setSelectedSecurity(rowData);
    setUpdateOpen(true);
  };

  const handleCloseUpdate = () => {
    setUpdateOpen(false);
    setSelectedSecurity(null);
  };

  const handleSecurityUpdated = () => {
    showSuccess("Security updated successfully.");
  };

  const handleSecurityCreated = () => {
    // No refetch — the new security arrives via the WS new-security event
    // (isNew=1) and is prepended in the WS handler.
    showSuccess("Security created successfully.");
  };

  const executeDownload = (format) => {
    setCsvLoading(true);
    return downloadSecMasterCSV(token, totalElements, selectedColumns, format, sortConfigForFetch, appliedFilters)
      .catch(() => showError("Failed to download sec master data. Please try again."))
      .finally(() => setCsvLoading(false));
  };

  const handleDownloadCSV = (format = "xlsx") => {
    // Above the row cap, warn the user that only the first MAX_DOWNLOAD_ROWS rows
    // will be exported and let them confirm before proceeding.
    if ((totalElements || 0) > MAX_DOWNLOAD_ROWS) {
      setPendingDownloadFormat(format);
      setDownloadConfirmOpen(true);
      return;
    }
    executeDownload(format);
  };

  const handleProceedDownload = () => {
    setDownloadConfirmOpen(false);
    executeDownload(pendingDownloadFormat);
  };

  const handleOpenSearch = () => {
    navigate("/search-sec-master", {
      state: appliedFilterData
        ? { secMasterFilterData: appliedFilterData, bondType: appliedFilterData.bondType }
        : undefined,
    });
  };

  const handleClearFilters = () => {
    setAppliedFilterData(null);
    setPage(0);
    navigate("/sec-master", { state: null });
  };

  const handleDeleteChip = (key) => {
    if (key === "type" || key === "bondType") {
      handleClearFilters();
      return;
    }

    let fieldsToClear;
    if (key.startsWith("min") || key.startsWith("max")) {
      const baseKey = key.replace(/^(min|max)/, "");
      fieldsToClear = [
        ...(SEC_MASTER_CHIP_TO_FILTER_FIELDS[`min${baseKey}`] || []),
        ...(SEC_MASTER_CHIP_TO_FILTER_FIELDS[`max${baseKey}`] || []),
      ];
      if (fieldsToClear.length === 0) fieldsToClear = [key];
    } else {
      fieldsToClear = SEC_MASTER_CHIP_TO_FILTER_FIELDS[key] || [key];
    }

    setAppliedFilterData((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      fieldsToClear.forEach((field) => {
        next[field] = "";
      });
      return next;
    });
    setPage(0);
  };

  return (
    <>
      <SecMasterHeader
        totalElements={displayedTotalElements}
        loading={loading}
        lastUpdatedAt={displayLastUpdatedAt}
        csvLoading={csvLoading}
        customizeButtonRef={customizeButtonRef}
        appliedFilters={appliedFilters}
        onSearch={handleOpenSearch}
        onAddSecurity={() => setCreateOpen(true)}
        onClearFilters={handleClearFilters}
        onDeleteChip={handleDeleteChip}
        onCustomizeClick={() => setCcOpen(true)}
        onDownloadCSV={handleDownloadCSV}
      />

      {dataError ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
          <Typography sx={{ color: tokens.palette.danger, fontSize: "18px" }}>
            {dataError}
          </Typography>
        </Box>
      ) : (
        <SecMasterTable
          secMasterData={mergedSecMasterData}
          selectedColumns={selectedColumns}
          page={page}
          itemsPerPage={itemsPerPage}
          totalElements={displayedTotalElements}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onCusipClick={handleCusipClick}
          onSort={handleSort}
          sortConfig={sortConfig}
        />
      )}

      <CustomizeColumns
        open={ccOpen}
        onClose={() => setCcOpen(false)}
        allColumns={allColumns}
        selectedColumns={selectedColumns}
        setSelectedColumnsInventory={setSelectedColumns}
        anchorEl={customizeButtonRef.current}
        sectionKey="sec-master"
        fixedColumnKeys={["identifier"]}
      />

      {updateOpen && (
        <UpdateSecurityPopup
          open={updateOpen}
          onClose={handleCloseUpdate}
          securityData={selectedSecurity}
          onUpdated={handleSecurityUpdated}
          fieldOptions={fieldOptions}
        />
      )}

      {createOpen && (
        <CreateSecurityPopup
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={handleSecurityCreated}
          fieldOptions={fieldOptions}
        />
      )}

      <SecMasterDownloadConfirmModal
        open={downloadConfirmOpen}
        onProceed={handleProceedDownload}
        onClose={() => setDownloadConfirmOpen(false)}
        loading={csvLoading}
        totalElements={totalElements}
        limit={MAX_DOWNLOAD_ROWS}
      />

      <SnackbarComponent />
    </>
  );
};

export default SecMasterScreen;
